import { useRef } from "react";
import type {
  CSSProperties,
  HTMLAttributes,
  KeyboardEvent,
  ReactNode,
} from "react";
import "./SegmentedControl.css";

type StyleWithVars = CSSProperties & Record<`--${string}`, string | number>;

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string> extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "onChange" | "children"
> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: "sm" | "md";
  /** Renders the icon only and moves the label to `aria-label`. */
  iconOnly?: boolean;
  /** Required unless an `aria-labelledby` is supplied. */
  "aria-label"?: string;
}

/**
 * Two-to-four exclusive options in a capsule track. Follows the ARIA radio
 * group pattern: one tab stop, arrows move *and* select, Home/End jump to the
 * ends, and disabled options are skipped.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = "md",
  iconOnly = false,
  className,
  ...rest
}: SegmentedControlProps<T>) {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const activeIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );

  const select = (index: number) => {
    const option = options[index];
    if (option === undefined || option.disabled === true) return;
    buttonRefs.current[index]?.focus();
    if (option.value !== value) onChange(option.value);
  };

  /** Walks over disabled options, wrapping at both ends. */
  const step = (from: number, delta: number) => {
    const count = options.length;
    for (let offset = 1; offset <= count; offset += 1) {
      const next = (from + delta * offset + count * offset) % count;
      const option = options[next];
      if (option !== undefined && option.disabled !== true) {
        select(next);
        return;
      }
    }
  };

  const edge = (direction: "first" | "last") => {
    const order =
      direction === "first"
        ? options.map((_, index) => index)
        : options.map((_, index) => options.length - 1 - index);
    for (const index of order) {
      const option = options[index];
      if (option !== undefined && option.disabled !== true) {
        select(index);
        return;
      }
    }
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        step(index, 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        step(index, -1);
        break;
      case "Home":
        event.preventDefault();
        edge("first");
        break;
      case "End":
        event.preventDefault();
        edge("last");
        break;
      default:
        break;
    }
  };

  const classes = [
    "fc-seg",
    `fc-seg--${size}`,
    iconOnly ? "fc-seg--icon" : null,
    className,
  ]
    .filter((entry): entry is string => Boolean(entry))
    .join(" ");

  const trackStyle: StyleWithVars = {
    "--fc-seg-count": options.length,
    "--fc-seg-index": activeIndex,
  };

  return (
    <div {...rest} className={classes} role="radiogroup" style={trackStyle}>
      <span className="fc-seg__thumb" aria-hidden="true" />
      {options.map((option, index) => (
        <button
          key={option.value}
          ref={(node) => {
            buttonRefs.current[index] = node;
          }}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          aria-label={iconOnly ? option.label : undefined}
          disabled={option.disabled}
          tabIndex={index === activeIndex ? 0 : -1}
          className="fc-seg__option"
          onClick={() => select(index)}
          onKeyDown={(event) => handleKeyDown(event, index)}
        >
          {option.icon !== undefined && option.icon !== null ? (
            <span className="fc-seg__icon" aria-hidden="true">
              {option.icon}
            </span>
          ) : null}
          {iconOnly ? null : (
            <span className="fc-seg__label">{option.label}</span>
          )}
        </button>
      ))}
    </div>
  );
}
